import { MailOutlineRounded as MailOutlineRoundedIcon } from "@mui/icons-material";
import { Box, CircularProgress, Typography } from "@mui/material";
import { EdmButton } from "atoms";
import { accessTokenKey, fetchProfile } from "misc/authProvider";
import { useErrorHandler, useInterval } from "misc/customHooks";
import { ExtendedDataProvider } from "misc/dataProvider.types";
import { BackendPermissionsType } from "misc/permission.types";
import { EdmCard } from "molecules";
import { ReactNode, useEffect } from "react";
import { useDataProvider, useNotify, usePermissions, useRefresh, useTranslate } from "react-admin";

type EdmLayoutPropsType = {
  children: ReactNode;
};

export default function PortalConsentCheck(props: EdmLayoutPropsType) {
  const { children } = props;
  const { permissions } = usePermissions<BackendPermissionsType>();
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<ExtendedDataProvider>();
  const refresh = useRefresh();
  const handleError = useErrorHandler();

  // Refetch profile every 10 seconds
  const refetchProfile = async () => {
    const token = localStorage.getItem(accessTokenKey);
    if (token) {
      try {
        await fetchProfile(token);
        refresh();
      } catch {
        // Retried by the interval; an expired token is handled by checkError
      }
    }
  };
  // Only run refetch when permissions are missing
  useInterval(!permissions?.acceptedConsentScopes?.includes("PORTAL") ? refetchProfile : () => {}, 10000);

  useEffect(() => {
    refetchProfile();
  }, []);

  if (!permissions) {
    return <CircularProgress />;
  }

  if (permissions.acceptedConsentScopes?.includes("PORTAL")) {
    return children;
  }

  const resendVerification = async () => {
    try {
      await dataProvider.resendConsentVerification("PORTAL");

      notify("ra.notification.created", { type: "info" });
    } catch (error) {
      handleError(error as Error);
    }
  };

  return (
    <Box maxWidth="768px" mx="auto">
      <EdmCard>
        <Typography variant="h4" component="h1" my={2} textAlign="center">
          {translate("portal.confirm_your_email")}
        </Typography>
        <Typography variant="subtitle1" component="h2" mb={4}>
          {translate("portal.missing_consent")}
        </Typography>
        <Box textAlign="center" mb={2}>
          <EdmButton label={translate("portal.resend_verification_mail")} onClick={resendVerification}>
            <MailOutlineRoundedIcon />
          </EdmButton>
        </Box>
      </EdmCard>
    </Box>
  );
}
